import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { merge } from 'lodash';

import { FormValidations } from '@hmax-order/frontend/utils';
import { DateUtils } from '@hmax-order/shared/date';
import { DialogBase, DialogPeriod, DialogPeriodForm } from '../../../interfaces';
import { InputDateComponent, InputDateRangeComponent } from '../../fields';
import { DialogBaseComponent } from '../dialog-base/dialog-base.component';

@Component({
  selector: 'hmax-dialog-period',
  standalone: true,
  templateUrl: './dialog-period.component.html',
  styleUrls: ['./dialog-period.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DialogBaseComponent,
    InputDateComponent,
    InputDateRangeComponent,
    MatDialogModule,
    ReactiveFormsModule,
    TranslocoModule,
  ],
})
export class DialogPeriodComponent implements OnInit {
  private _data = inject<DialogPeriod>(MAT_DIALOG_DATA);
  private _dialogRef = inject(MatDialogRef<DialogPeriodComponent>);
  private _formBuilder = inject(FormBuilder);
  private _translocoService = inject(TranslocoService);

  form!: FormGroup;
  min!: Date;
  max!: Date;
  typeInput = 'range';
  formControl: DialogPeriodForm = {
    start: {
      label: 'start',
      labelTranslate: true,
      name: 'start',
      validators: [],
    },
    end: {
      label: 'end',
      labelTranslate: true,
      name: 'end',
      validators: [],
    },
  };

  get dateRangeLabel(): string {
    let label = '';

    label += this.formControl?.start?.labelTranslate ? this.translate(this.formControl?.start?.label) : this.formControl?.start?.label;

    label += ' - ';

    label += this.formControl?.end?.labelTranslate ? this.translate(this.formControl?.end?.label) : this.formControl?.end?.label;

    return label;
  }

  constructor() {
    if (this._data?.formControl) {
      const start = { ...this.formControl?.start, ...this._data?.formControl?.start };
      const end = { ...this.formControl?.end, ...this._data?.formControl?.end };
      this.formControl = { ...this.formControl, ...this._data?.formControl, start, end };
    }
    this.typeInput = this._data?.typeInput ?? 'range';
  }

  /**
   * Abrir um modal deste componente com as configurações padrão
   * Precisa receber uma instância do MatDialog
   * ex: DialogPeriodComponent.open(this._matDialog);
   * @param matDialog Instância de MatDialog do Angular Material que deve ser injetada
   * pela classe que invocar este método
   */
  static open(matDialog: MatDialog, data?: DialogPeriod): MatDialogRef<DialogPeriodComponent> {
    const defaultConfig: DialogBase = {
      actions: {
        confirm: {
          color: 'primary',
          label: data?.confirmLabel ?? 'ok',
          labelTranslate: true,
          show: true,
          action: () => dialogRef.componentInstance.save(),
        },
        cancel: {
          label: data?.cancelLabel ?? 'cancel',
          labelTranslate: true,
          show: true,
        },
      },
    };

    const config = merge({}, defaultConfig, data);

    const dialogRef = matDialog.open(DialogPeriodComponent, {
      autoFocus: true,
      width: data?.width,
      data: { matDialog, ...config },
      disableClose: false,
    });

    return dialogRef;
  }

  ngOnInit(): void {
    this.verifyConfig();
    this.createForm();
  }

  private verifyConfig(): void {
    const currentDate = DateUtils.currentDate();

    if (!this._data?.notMinDate) {
      const minDays = this._data?.minDays ?? 30;
      this.min = this._data?.min ?? currentDate.minus({ days: minDays }).toJSDate();
    }

    if (!this._data?.notMaxDate) {
      const maxDays = this._data?.maxDays ? currentDate.plus({ days: this._data.maxDays }).toJSDate() : currentDate.toJSDate();
      this.max = this._data?.max ?? maxDays;
    }
  }

  private createForm(): void {
    const { start, end } = this.formControl;

    if (start && end) {
      const startValidators = this._data.maxPeriodDays
        ? merge(start?.validators, [FormValidations.verifyMaxPeriodDays('start', String(end.name), this._data.maxPeriodDays)])
        : start?.validators;
      const endValidators = this._data.maxPeriodDays
        ? merge(start?.validators, [FormValidations.verifyMaxPeriodDays('end', String(start.name), this._data.maxPeriodDays)])
        : end?.validators;

      this.form = this._formBuilder.group({
        [String(start.name)]: [start?.value ?? null, startValidators],
        [String(end.name)]: [end?.value ?? null, endValidators],
      });
    }
  }

  private translate(text?: string): string {
    return text ? this._translocoService.translate(text) : String(text ?? '');
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.form.markAsDirty();
      return;
    }

    this._dialogRef.close(this.form?.value);
  }
}
